// Single source of truth. Everything (UI, API, scoring, cron) reads from here.
// Confirmed Year 2 values.

export type DuplicateRule =
  | "one_per_ticker_direction" // block two same-direction holds; allow LONG + SHORT
  | "one_per_ticker" // a name can be held only once league-wide, any direction
  | "none"; // no restriction

export const LEAGUE = {
  brandName: "The Stock Combine", // shown in the header + broadcast title
  seasonName: "Season 2", // the season label (e.g. on Standings)
  heroImage: "/hero.jpg", // drop the thumbnail at public/hero.jpg (hidden if absent)
  streamUrl: "https://www.youtube.com/watch?v=lzP9chyox80", // hero links here

  numManagers: 6,
  numRounds: 12, // => 12 positions per manager, 72 total picks
  positionsPerManager: 12,
  scoring: "equal_weight" as const, // manager return = simple average across their 12 slots

  // Round-1 draft order = Year 1 final standings (1st picks first).
  // Year 1 total returns: Amit 81.43, Steve 68.49, Matt 57.21, Kris 45.62, Tannor 14.18.
  // Jason is the new 6th manager (no Year 1 result) — seeded last. CONFIRMED.
  round1Order: ["Amit", "Steve", "Matt", "Kris", "Tannor", "Jason"],
  managers: ["Amit", "Steve", "Matt", "Kris", "Tannor", "Jason"],

  // Baseline = price captured live when each pick locks (current quote, or that
  // day's close after hours). CONFIRMED. A fixed date can still be set here.
  draftDate: null as string | null, // "YYYY-MM-DD" or null for live stamping

  // CONFIRMED: a (ticker, direction) pair may be held only once league-wide, so
  // two managers can't both be LONG the same name, but LONG + SHORT is allowed.
  duplicateRule: "one_per_ticker_direction" as DuplicateRule,

  marketHours: { open: "09:30", close: "16:00", tz: "America/New_York" },
  pollIntervalSec: 60, // cron quote-refresh cadence during market hours
  // Client poll cadences. Kept conservative on purpose: every open tab hits the
  // Worker on each tick, and many viewers during a live stream can otherwise blow
  // through Cloudflare's free-plan request cap. Picks change at most once per ~60s
  // (the pick clock), and standings track the market which the cron only refreshes
  // every 60s — so faster polling buys no freshness, just request volume.
  draftPollSec: 8, // client poll cadence for live draft state
  leaderboardPollSec: 60, // client poll cadence for standings
  pickClockSec: 60, // per-pick countdown clock (starts when each pick locks)

  // Finnhub quotes are one call per ticker (60/min free). The cron polls held
  // tickers in rotating batches of this size each minute to stay under that and
  // the Worker free-plan subrequest cap.
  quotePollBatch: 30,
  // Dividends are one call per ticker (Yahoo, FMP as backup — up to 2 fetches
  // each). The daily pass walks every held ticker this many per cron minute,
  // keeping worst-case fetches under the Worker free-plan subrequest cap (50).
  dividendPullBatch: 20,

  // Rebalance: each manager may discard up to 2 and pick up to 2 per event.
  maxRebalancePerManager: 2,
} as const;

/** Per-manager social links, keyed by manager name. Any field may be omitted. */
export type ManagerSocial = {
  youtube?: string;
  x?: string;
  seekingAlpha?: string;
  website?: string;
};
export const MANAGER_SOCIALS: Record<string, ManagerSocial> = {
  Amit: {
    youtube: "https://www.youtube.com/@amitinvesting",
    x: "https://x.com/amitisinvesting",
  },
  Steve: {
    youtube: "https://www.youtube.com/@stevenfiorillo1",
    x: "https://x.com/stevenfiorillo",
    seekingAlpha: "https://seekingalpha.com/author/steven-fiorillo",
  },
  Matt: {
    youtube: "https://www.youtube.com/@RealMattMoney",
    x: "https://x.com/RealMattMoney",
  },
  Kris: {
    x: "https://x.com/KrisPatel99",
  },
  Tannor: {
    youtube: "https://www.youtube.com/@futurenvesting",
    x: "https://x.com/Futurenvesting",
  },
  Jason: {
    youtube: "https://www.youtube.com/@papergainsinc",
    x: "https://x.com/PaperGainsInc",
    website: "https://school-of-gains.com/yto",
  },
};

export const TOTAL_PICKS = LEAGUE.numManagers * LEAGUE.numRounds;

/**
 * Rotation draft (NOT a snake). Each round shifts the order left by one.
 * Over 12 rounds with 6 managers each manager occupies each seat exactly twice.
 * `round` is 1-indexed.
 */
export function draftOrder(round: number): string[] {
  const o = LEAGUE.round1Order;
  const shift = (round - 1) % o.length;
  return o.slice(shift).concat(o.slice(0, shift));
}

/** 1-indexed round for a 1-indexed overall pick number (1..72). */
export function roundForPick(pick: number): number {
  return Math.floor((pick - 1) / LEAGUE.numManagers) + 1;
}

/** 1-indexed seat within the round for a 1-indexed pick number. */
export function seatForPick(pick: number): number {
  return ((pick - 1) % LEAGUE.numManagers) + 1;
}

/** Manager name on the clock for a given overall pick number. */
export function managerForPick(pick: number): string {
  return draftOrder(roundForPick(pick))[seatForPick(pick) - 1];
}

/** Overall pick number for a given (managerName, round). Inverse of the above. */
export function pickNumberFor(managerName: string, round: number): number {
  const seat = draftOrder(round).indexOf(managerName) + 1;
  return (round - 1) * LEAGUE.numManagers + seat;
}
